export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type ErrorBody = { error?: string };

function jsonBody(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let message = res.statusText || "request failed";
    try {
      const body = (await res.json()) as ErrorBody;
      if (body.error) message = body.error;
    } catch {
      // ignore non-JSON error bodies
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export type Health = { status: string };
export type User = { id: string; email: string; name: string };
export type Me = { authenticated: boolean; user: User | null };
/** A named from–to range of the waking day (e.g. High 80–90%). */
export type EnergyLevel = {
  id: string;
  name: string;
  /** Lower bound of the band; equals `percent` for a single-value level. */
  percent_min: number;
  /** Upper bound of the band: the Day chart's 100%. */
  percent: number;
  /** "80–90%" or "90%", formatted by the server. */
  range: string;
  sort_order: number;
  /** Computed by the server: the highest upper bound. Never stored. */
  is_default: boolean;
};

/** "80–90%" or "90%" for any object carrying a band. */
export function energyRange(level: {
  percent_min: number;
  percent: number;
}): string {
  return level.percent_min === level.percent
    ? `${level.percent}%`
    : `${level.percent_min}–${level.percent}%`;
}

/** Where planned time sits against the Target Load band. */
export type CapacityStatus = "under" | "in_band" | "over";

export type Label = {
  id: string;
  name: string;
  description: string;
  /** Google Calendar event colorId; resolve to hex via api.colors(). */
  google_color_id: string;
  sort_order: number;
  /** Ideal Day share, 0 = no target. Edited via api.updateIdealDay. */
  ideal_percent: number;
  /** Built-in Label; cannot be deleted. */
  is_system: boolean;
  /** "investment" collects Routines; "external" marks appointments you are
   *  not part of (shown on the timeline, left out of capacity). */
  system_role: "investment" | "external" | "chore" | "";
};

export type RoutineAction = {
  id?: string;
  title: string;
  duration_minutes: number;
  priority: number;
};

/** A small set of Actions run back to back in one Calendar slot. */
export type RoutineType = "morning" | "evening";

export type Routine = {
  id: string;
  title: string;
  type: RoutineType;
  description: string;
  total_minutes: number;
  actions: RoutineAction[];
};

export type RoutineInput = {
  title: string;
  type: RoutineType;
  description: string;
  actions: { title: string; duration_minutes: number; priority: number }[];
};

export type RoutineStart = {
  activity_id: string;
  google_event_id: string;
  routine_id: string;
  title: string;
  start_at: string;
  end_at: string;
  total_minutes: number;
  chosen: RoutineAction[];
  skipped: RoutineAction[];
  label_id: string;
};

export type Settings = {
  day_start: string;
  day_end: string;
  project_session_minutes: number;
  /** Minutes counted after every block in the Day chart. */
  task_margin_minutes: number;
  waking_minutes: number;
  default_energy_level_id: string | null;
  energy_levels: EnergyLevel[];
  labels: Label[];
};

export type WakingHoursInput = {
  day_start: string;
  day_end: string;
  project_session_minutes: number;
  task_margin_minutes: number;
};

export type EnergyLevelInput = {
  name: string;
  percent_min: number;
  percent: number;
};

export type LabelInput = {
  name: string;
  description: string;
  google_color_id: string;
};

export type APIToken = {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
};

export type IdealDayInput = {
  targets: { label_id: string; percent: number }[];
};

export type ProjectOutcome = "completed" | "not_completed";

export type TemplateType = "repeating" | "project" | "once_off";

/** A Wishlist idea is just text; Type and Labels are chosen on promotion. */
export type WishlistItem = {
  id: string;
  title: string;
  created_at: string;
};

export type WishlistInput = { title: string };

/** The Template created by promoting a Wishlist idea. */
export type PromotedTemplate = {
  id: string;
  type: TemplateType;
  title: string;
  duration_minutes: number;
  sort_order: number;
  is_priority: boolean;
  label_ids: string[];
};

/** An external resource on a Template, e.g. the project plan. */
export type Link = { title: string; url: string };

export type Template = {
  id: string;
  type: TemplateType;
  title: string;
  description: string;
  /** Repeating/OnceOff: Activity length. Project: remaining effort. */
  duration_minutes: number;
  /** "HH:MM" or "" for none. */
  suggested_start: string;
  sort_order: number;
  is_priority: boolean;
  /** Repeating only: whether the Streak moves and is shown. */
  track_streak: boolean;
  /** Repeating Templates only, 0–5; moves only in a Planning Session. */
  streak: number;
  label_ids: string[];
  /** Carried into the Calendar event description. */
  links: Link[];
  /** Projects only: "YYYY-MM-DD" or "". The day after, the project closes. */
  deadline: string;
};

export type TemplateInput = {
  type: TemplateType;
  title: string;
  description: string;
  duration_minutes: number;
  suggested_start: string;
  sort_order: number;
  is_priority: boolean;
  track_streak: boolean;
  label_ids: string[];
  links: Link[];
  /** Projects only; "" for none. */
  deadline: string;
};

/** One Label's column in the library, already ordered by the server. */
export type LibraryColumn = { label_id: string; template_ids: string[] };

export type TemplateLibrary = {
  templates: Template[];
  columns: LibraryColumn[];
};

export type Activity = {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  color_hex: string;
  google_event_id: string;
};

export type ActivityList = {
  time_zone: string;
  activities: Activity[];
};

export type ScheduledActivity = Activity & {
  template_id?: string;
  display_label_id?: string;
  /** Not your own time: drawn faded, excluded from the Day chart. */
  external?: boolean;
  /** An ended block Leno scheduled (assumed done): can be clicked to
   *  reschedule it. */
  reschedulable?: boolean;
  /** Could not be rescheduled (no free slot left): carries the Missed label. */
  missed?: boolean;
  /** Refined into a checklist of steps (a planned block). */
  refined?: boolean;
  steps?: Step[];
};

/** One line of a refined block, e.g. "Scales" for 10 minutes. */
export type Step = { text: string; minutes?: number };

/** The Today screen's data: the day's Activities plus capacity and coverage. */
export type Today = {
  date: string;
  time_zone: string;
  day_start: string;
  day_end: string;
  waking_minutes: number;
  energy_level: EnergyLevel | null;
  /** The Target Load band in minutes: target_min_minutes–target_load_minutes.
   *  target_load_minutes is the upper bound, the Day chart's 100%. */
  target_min_minutes: number;
  target_load_minutes: number;
  planned_minutes: number;
  /** True above the upper bound only. */
  over_planned: boolean;
  capacity_status: CapacityStatus;
  /** Minutes that still fit before the lower bound (0 unless under). */
  room_minutes: number;
  /** Margin counted after every block (per block, and in total). */
  margin_minutes: number;
  margin_total_minutes: number;
  by_label: { label_id: string; minutes: number }[];
  /** Numeric Mix: full duration under every Label an Activity carries. */
  mix: { label_id: string; minutes: number }[];
  activities: ScheduledActivity[];
  day_plan: DayPlan;
};

export type WeekDay = {
  date: string;
  score: number | null;
  diary_note: string;
  reviewed: boolean;
  energy_level: {
    id: string;
    name: string;
    percent_min: number;
    percent: number;
  } | null;
  is_today: boolean;
  is_future: boolean;
  is_vacation: boolean;
};

export type Week = {
  start: string;
  end: string;
  time_zone: string;
  days: WeekDay[];
  by_label: { label_id: string; minutes: number; percent: number }[];
  total_minutes: number;
};

/** An assistant's take on a day, recorded over MCP before the user scores. */
export type DayAssessment = {
  summary: string;
  suggested_score: number | null;
  assessed_at: string;
};

export type DayPlan = {
  day: string;
  energy_level_id: string | null;
  score: number | null;
  diary_note: string;
  reviewed: boolean;
  assessment: DayAssessment | null;
  /** A vacation day: no planning, no efficiency calculated. */
  is_vacation: boolean;
};

/** An unresolved past Activity awaiting a look-back classification. */
export type LookBackActivity = {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  color_hex: string;
  label_ids: string[];
  display_label_id?: string;
  template?: {
    id: string;
    type: TemplateType;
    title: string;
    streak: number;
    track_streak: boolean;
    remaining_minutes: number;
  };
};

export type LookBack = {
  items: LookBackActivity[];
  reviewed_day: string;
  today: string;
  time_zone: string;
  day_plan: DayPlan;
};

export type Resolution = "done" | "carry" | "drop";

export type ResolveResult = {
  activity_id: string;
  resolution: Resolution;
  template: Template | null;
  project_depleted: boolean;
  carried: { google_event_id: string; start_at: string; end_at: string } | null;
  carry_error?: string;
};

/** A block on the week planner. `removable`: future and created by Leno. */
export type PlannerActivity = ScheduledActivity & { removable: boolean };

export type PlannerDay = {
  date: string;
  is_today: boolean;
  is_past: boolean;
  activities: PlannerActivity[];
};

export type Planner = {
  start: string;
  end: string;
  time_zone: string;
  day_start: string;
  days: PlannerDay[];
};

export type RegimenEntry = {
  id?: string;
  template_id: string;
  start_time: string;
};

export type Regimen = { id: string; name: string; entries: RegimenEntry[] };

export type RegimenInput = { name: string; entries: RegimenEntry[] };

/** A block a Regimen creates (or would create, on a dry run) or skips. */
export type RegimenBlock = {
  title: string;
  start_at: string;
  end_at: string;
  reason?: string;
};

export type RegimenApplication = {
  dry_run: boolean;
  created: RegimenBlock[];
  skipped: RegimenBlock[];
};

export type ScheduleInput =
  | { template_id: string; display_label_id?: string; start_at: string }
  | { title: string; start_at: string; end_at: string; color_hex: string };

export type CalendarColor = { id: string; hex: string };

export const api = {
  health: () => request<Health>("/api/health"),
  me: () => request<Me>("/api/me"),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  settings: () => request<Settings>("/api/settings"),
  updateSettings: (body: WakingHoursInput) =>
    request<Settings>("/api/settings", jsonBody("PUT", body)),
  createEnergyLevel: (body: EnergyLevelInput) =>
    request<EnergyLevel>("/api/settings/energy-levels", jsonBody("POST", body)),
  updateEnergyLevel: (id: string, body: EnergyLevelInput) =>
    request<EnergyLevel>(
      `/api/settings/energy-levels/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteEnergyLevel: (id: string) =>
    request<void>(`/api/settings/energy-levels/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  tokens: () => request<{ tokens: APIToken[] }>("/api/settings/tokens"),
  createToken: (name: string) =>
    request<APIToken & { token: string }>(
      "/api/settings/tokens",
      jsonBody("POST", { name }),
    ),
  revokeToken: (id: string) =>
    request<void>(`/api/settings/tokens/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  updateIdealDay: (body: IdealDayInput) =>
    request<Settings>("/api/settings/ideal-day", jsonBody("PUT", body)),
  labels: () => request<{ labels: Label[] }>("/api/labels"),
  createLabel: (body: LabelInput) =>
    request<Label>("/api/labels", jsonBody("POST", body)),
  updateLabel: (id: string, body: LabelInput) =>
    request<Label>(
      `/api/labels/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteLabel: (id: string) =>
    request<void>(`/api/labels/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  templates: () => request<TemplateLibrary>("/api/templates"),
  createTemplate: (body: TemplateInput) =>
    request<Template>("/api/templates", jsonBody("POST", body)),
  updateTemplate: (id: string, body: TemplateInput) =>
    request<Template>(
      `/api/templates/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteTemplate: (id: string, outcome?: ProjectOutcome) =>
    request<void>(
      `/api/templates/${encodeURIComponent(id)}${outcome ? `?outcome=${outcome}` : ""}`,
      {
        method: "DELETE",
      },
    ),
  wishlist: () => request<{ items: WishlistItem[] }>("/api/wishlist"),
  createWishlist: (body: WishlistInput) =>
    request<WishlistItem>("/api/wishlist", jsonBody("POST", body)),
  updateWishlist: (id: string, body: WishlistInput) =>
    request<WishlistItem>(
      `/api/wishlist/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteWishlist: (id: string) =>
    request<void>(`/api/wishlist/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  promoteWishlist: (id: string, type: TemplateType, labelIds: string[]) =>
    request<{ template: PromotedTemplate }>(
      `/api/wishlist/${encodeURIComponent(id)}/promote`,
      jsonBody("POST", { type, label_ids: labelIds }),
    ),
  activities: (scope?: "planning") =>
    request<ActivityList>(
      scope ? `/api/activities?scope=${scope}` : "/api/activities",
    ),
  colors: () => request<{ colors: CalendarColor[] }>("/api/calendar/colors"),
  lookBack: () => request<LookBack>("/api/planning/lookback"),
  resolveActivity: (activityId: string, resolution: Resolution) =>
    request<ResolveResult>(
      "/api/planning/resolve",
      jsonBody("POST", { activity_id: activityId, resolution }),
    ),
  projectDecision: (
    templateId: string,
    decision: "complete" | "keep",
    remainingMinutes?: number,
  ) =>
    request<{ decision: string; template: Template | null }>(
      "/api/planning/project-decision",
      jsonBody("POST", {
        template_id: templateId,
        decision,
        remaining_minutes: remainingMinutes ?? 0,
      }),
    ),
  saveDayReview: (day: string, score: number, diaryNote: string) =>
    request<DayPlan>(
      "/api/planning/review",
      jsonBody("PUT", { day, score, diary_note: diaryNote }),
    ),
  setDayEnergyLevel: (day: string, energyLevelId: string) =>
    request<DayPlan>(
      "/api/planning/energy-level",
      jsonBody("PUT", { day, energy_level_id: energyLevelId }),
    ),
  setActivitySteps: (id: string, steps: Step[]) =>
    request<{ steps: Step[]; refined: boolean }>(
      `/api/activities/${encodeURIComponent(id)}/steps`,
      jsonBody("PUT", { steps }),
    ),
  setDayVacation: (day: string, vacation: boolean) =>
    request<DayPlan>(
      "/api/planning/vacation",
      jsonBody("PUT", { day, vacation }),
    ),
  routines: () => request<{ routines: Routine[] }>("/api/routines"),
  createRoutine: (body: RoutineInput) =>
    request<Routine>("/api/routines", jsonBody("POST", body)),
  updateRoutine: (id: string, body: RoutineInput) =>
    request<Routine>(
      `/api/routines/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteRoutine: (id: string) =>
    request<void>(`/api/routines/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  regimens: () => request<{ regimens: Regimen[] }>("/api/regimens"),
  createRegimen: (body: RegimenInput) =>
    request<Regimen>("/api/regimens", jsonBody("POST", body)),
  updateRegimen: (id: string, body: RegimenInput) =>
    request<Regimen>(
      `/api/regimens/${encodeURIComponent(id)}`,
      jsonBody("PUT", body),
    ),
  deleteRegimen: (id: string) =>
    request<void>(`/api/regimens/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  applyRegimen: (
    id: string,
    body: { week: "current" | "next"; days: string[]; dry_run: boolean },
  ) =>
    request<RegimenApplication>(
      `/api/regimens/${encodeURIComponent(id)}/apply`,
      jsonBody("POST", body),
    ),
  startRoutine: (id: string, actionIds: string[]) =>
    request<RoutineStart>(
      `/api/routines/${encodeURIComponent(id)}/start`,
      jsonBody("POST", { action_ids: actionIds }),
    ),
  week: (start?: string) =>
    request<Week>(
      start ? `/api/week?start=${encodeURIComponent(start)}` : "/api/week",
    ),
  /** One-field quick add: starts now, 30 min, Chore Label. */
  quickChore: (title: string) =>
    request<{
      status: string;
      title: string;
      start_at: string;
      end_at: string;
    }>("/api/activities/chore", jsonBody("POST", { title })),
  planner: (week: "current" | "next" = "current") =>
    request<Planner>(`/api/planner?week=${week}`),
  /** Removes a future Leno-created block from Google Calendar and Leno. */
  deleteActivity: (id: string) =>
    request<void>(`/api/activities/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  /** Moves a past block to the next free slot later today; with none left it
   *  is marked Missed instead (no_slot). */
  rescheduleActivity: (id: string) =>
    request<{
      activity_id: string;
      no_slot: boolean;
      start_at?: string;
      end_at?: string;
    }>(`/api/activities/${encodeURIComponent(id)}/reschedule`, {
      method: "POST",
    }),
  today: (date?: string) =>
    request<Today>(
      date ? `/api/today?date=${encodeURIComponent(date)}` : "/api/today",
    ),
  scheduleActivity: (body: ScheduleInput) =>
    request<{
      status: string;
      google_event_id: string;
      start_at: string;
      end_at: string;
    }>("/api/activities", jsonBody("POST", body)),
};
